/* ============================================================
   VD Real Results — "Stop shedding. Start growing" timeline
   Scroll-driven: the timeline pins (position: sticky) inside a taller
   runway; scroll progress picks the active step, moves the copper bar,
   swaps the photo (crossfade desktop / slide mobile). Step titles jump to
   their scroll point.
   Desktop and phones both pin. A phone screen is shorter than the block (photo +
   step titles + the open step's text), so below 990px the photo shrinks, keeping
   its ratio, until the whole block fits. When even the smallest photo won't fit
   (short phones, landscape) nothing pins and the steps switch on tap.
   Vanilla JS, no deps, idempotent, theme-editor aware.
   ============================================================ */
(function () {
  'use strict';

  if (window.__vdRrtLoaded) return;
  window.__vdRrtLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktop = window.matchMedia('(min-width: 990px)');

  /* Phones and tablets: smallest photo height worth pinning for, and the air kept above and
     below the pinned block. */
  var MIN_PHOTO = 200;
  var AIR = 12;
  var PHONE_VARS = ['--vd-rrt-media-h', '--vd-rrt-media-w', '--vd-rrt-steps-h'];
  var PIN_VARS = ['--vd-rrt-avail', '--vd-rrt-top', '--vd-rrt-sticky-h', '--vd-rrt-left', '--vd-rrt-width'].concat(PHONE_VARS);

  var PIN_MARGIN = 80;

  /* Snap a CSS length to the device pixel grid (125% / 150% Windows scaling is not a whole number). */
  function snap(value) {
    var dpr = window.devicePixelRatio || 1;
    return Math.round(value * dpr) / dpr;
  }

  function clearVars(section, names) {
    names.forEach(function (name) { section.style.removeProperty(name); });
  }

  function headerHeight() {
    var header = document.querySelector('.header-wpr');
    if (header) return header.getBoundingClientRect().height;
    var main = document.getElementById('MainContent');
    return main ? parseFloat(getComputedStyle(main).paddingTop) || 0 : 0;
  }

  function init(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-vd-rrt]').forEach(function (section) {
      if (section.dataset.vdRrtInit === '1') return;
      section.dataset.vdRrtInit = '1';

      var runway = section.querySelector('[data-vd-rrt-runway]');
      var sticky = section.querySelector('[data-vd-rrt-sticky]');
      var steps = Array.prototype.slice.call(section.querySelectorAll('[data-vd-rrt-step]'));
      var slides = Array.prototype.slice.call(section.querySelectorAll('[data-vd-rrt-slide]'));
      if (!runway || !sticky || !steps.length) return;

      var count = steps.length;
      var active = -1;
      var top = 0;
      var stickyH = 0;
      var canPin = count > 1 && 'IntersectionObserver' in window;
      var pinned = false;
      var fixed = false;

      /* While the block is pinned, hold it with position: fixed instead of position: sticky. At
         fractional display scaling (Windows 125% / 150%) the browser rounds a sticky element's
         position differently from one scroll offset to the next, so the whole pinned block jumped
         by a pixel while scrolling. A fixed block does not depend on the scroll offset.
         Sticky stays the base style and only takes over within PIN_MARGIN of either end of the
         runway: there the browser pins and releases the block exactly on the right frame, and the
         switch to fixed happens where both sit at the same spot, so it is not visible. */
      function setFixed(on) {
        if (on === fixed) return;
        fixed = on;
        section.classList.toggle('is-fixed', on);
      }

      function setActive(i) {
        if (i === active) return;
        active = i;
        section.style.setProperty('--vd-rrt-i', i);
        steps.forEach(function (step, k) {
          var on = k === i;
          step.classList.toggle('is-active', on);
          var head = step.querySelector('[data-vd-rrt-head]');
          if (head) head.setAttribute('aria-expanded', on ? 'true' : 'false');
        });
        slides.forEach(function (slide, k) { slide.classList.toggle('is-active', k === i); });
      }

      /* Phones: size the photo so the pinned block fits the screen. Measured against the natural
         layout (the sizing variables are cleared first) and against the tallest step text, and the
         steps box is then held at that height so the block doesn't change size from step to step.
         Returns false when even the smallest photo won't fit. */
      function fitSmall(avail) {
        clearVars(section, PHONE_VARS);
        var media = section.querySelector('.vd-rrt__media');
        var stepsBox = section.querySelector('.vd-rrt__steps');
        var bodies = steps.map(function (step) { return step.querySelector('.vd-rrt__step-body'); });
        if (!media || !stepsBox) return false;

        var mediaW = media.offsetWidth;
        var mediaH = media.offsetHeight;
        var open = bodies[active] ? bodies[active].offsetHeight : 0;
        section.classList.add('is-measuring');
        var tallest = bodies.reduce(function (max, body) { return Math.max(max, body ? body.offsetHeight : 0); }, 0);
        section.classList.remove('is-measuring');

        var extra = tallest - open;
        var stepsH = stepsBox.offsetHeight + extra;
        var rest = sticky.getBoundingClientRect().height - mediaH + extra;
        var photoH = Math.min(mediaH, Math.floor(avail - 2 * AIR - rest));
        if (photoH < MIN_PHOTO) return false;

        section.style.setProperty('--vd-rrt-steps-h', stepsH + 'px');
        if (photoH < mediaH) {
          section.style.setProperty('--vd-rrt-media-h', photoH + 'px');
          section.style.setProperty('--vd-rrt-media-w', Math.round(photoH * mediaW / mediaH) + 'px');
        }
        return true;
      }

      function layout() {
        /* Measure in the natural layout. */
        setFixed(false);
        var head = headerHeight();
        var avail = window.innerHeight - head;
        if (!canPin) {
          pinned = false;
        } else if (desktop.matches) {
          clearVars(section, PHONE_VARS);
          pinned = true;
        } else {
          pinned = fitSmall(avail);
        }
        if (!pinned) {
          section.classList.remove('is-pinned');
          clearVars(section, PIN_VARS);
          return;
        }
        section.style.setProperty('--vd-rrt-avail', avail + 'px');
        section.classList.add('is-pinned');
        stickyH = sticky.getBoundingClientRect().height;
        top = snap(head + Math.max(0, (avail - stickyH) / 2));
        var box = runway.getBoundingClientRect();
        section.style.setProperty('--vd-rrt-top', top + 'px');
        section.style.setProperty('--vd-rrt-left', snap(box.left) + 'px');
        section.style.setProperty('--vd-rrt-width', snap(box.width) + 'px');
        section.style.setProperty('--vd-rrt-sticky-h', stickyH + 'px');
      }

      /* The runway is built from the height measured in layout(), so measure the distance
         against that same height: the live height moves a few px when the step text
         changes, which nudged the progress back and forth across a step boundary. */
      function travel() { return runway.getBoundingClientRect().height - stickyH; }

      function update() {
        if (!pinned) return;
        var box = runway.getBoundingClientRect();
        setFixed(top - box.top >= PIN_MARGIN && box.bottom - stickyH - top >= PIN_MARGIN);
        var distance = travel();
        var p = distance > 0 ? (top - box.top) / distance : 0;
        p = Math.min(Math.max(p, 0), 1);
        setActive(Math.min(count - 1, Math.floor(p * count)));
      }

      steps.forEach(function (step, i) {
        var head = step.querySelector('[data-vd-rrt-head]');
        if (!head) return;
        head.addEventListener('click', function () {
          if (!pinned) { setActive(i); return; }
          var y = window.scrollY + runway.getBoundingClientRect().top - top + ((i + 0.5) / count) * travel();
          window.scrollTo({ top: y, behavior: reduceMotion ? 'auto' : 'smooth' });
        });
      });

      /* Run in the scroll event itself (it fires at most once per frame, before the frame is drawn) so
         the block switches between in-flow and fixed on the same frame the scroll offset changes. */
      function onScroll() { update(); }

      /* On phones the browser bar sliding in and out of view fires resize with only the height
         changed; re-fitting then would move the pinned block mid-scroll, so only a width change
         (rotation, window resize) re-lays out below 990px. */
      var resizeTimer;
      var lastWidth = window.innerWidth;
      window.addEventListener('resize', function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          var width = window.innerWidth;
          if (!desktop.matches && width === lastWidth) return;
          lastWidth = width;
          layout();
          update();
        }, 150);
      });
      window.addEventListener('scroll', onScroll, { passive: true });

      /* The step text can wrap differently once the web fonts arrive, which changes the heights
         measured above; fit again. */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function () { layout(); update(); });
      }

      /* Theme editor: show the selected step. */
      section.addEventListener('shopify:block:select', function (e) {
        var i = steps.indexOf(e.target);
        if (i > -1) setActive(i);
      });

      setActive(0);
      layout();
      update();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
